import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../../shared/api/api.tokens';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';

// Stats
export interface SystemAdminStats {
  totalBusinesses: number;
  activeBusinesses: number;
  businessesWithActiveMembership: number;
  businessesInTrial: number;
  businessesWithExpiredMembership: number;
  newBusinessesLast7Days: number;
  newBusinessesLast30Days: number;
}

// Business
export interface SystemAdminBusiness {
  businessId: string;
  name: string;
  isActive: boolean;
  membershipStatus: string;
  membershipPlanId?: string;
  membershipPlanName?: string;
  membershipStartDate?: string;
  membershipEndDate?: string;
  trialStartDate?: string;
  trialEndDate?: string;
  createdAt: string;
}

export interface GetSystemAdminBusinessesListResponse {
  businesses: SystemAdminBusiness[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Impersonation
export interface ImpersonateBusinessRequest {
  businessId: string;
}

export interface ImpersonateBusinessResponse {
  token: string;
  expiresAt: string;
  businessId: string;
  businessName: string;
  isImpersonating: boolean;
}

export interface StopImpersonationResponse {
  token: string;
  expiresAt: string;
}

// Audit Log
export interface AuditLogEntry {
  auditLogId: string;
  actionType: string;
  businessId: string;
  businessName: string;
  performedByUserId: string;
  performedByEmail: string;
  details?: string;
  createdAt: string;
}

export interface GetAuditLogResponse {
  entries: AuditLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class SystemAdminService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  private authContext() {
    return { context: new HttpContext().set(REQUIRE_AUTH, true) };
  }

  // Stats
  getStats(): Observable<SystemAdminStats> {
    return this.http.get<SystemAdminStats>(
      `${this.baseUrl}/api/v1/system-admin/stats`,
      this.authContext()
    );
  }

  // Businesses
  getBusinessesList(params?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    isActive?: boolean;
    membershipStatus?: string;
    membershipPlanName?: string;
  }): Observable<GetSystemAdminBusinessesListResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
    if (params?.searchTerm) httpParams = httpParams.set('searchTerm', params.searchTerm);
    if (params?.isActive !== undefined) httpParams = httpParams.set('isActive', params.isActive.toString());
    if (params?.membershipStatus) httpParams = httpParams.set('membershipStatus', params.membershipStatus);
    if (params?.membershipPlanName) httpParams = httpParams.set('membershipPlanName', params.membershipPlanName);

    return this.http.get<GetSystemAdminBusinessesListResponse>(
      `${this.baseUrl}/api/v1/system-admin/businesses`,
      { params: httpParams, ...this.authContext() }
    );
  }

  // Business Actions
  activateBusiness(businessId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/activate`,
      {},
      this.authContext()
    );
  }

  deactivateBusiness(businessId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/deactivate`,
      {},
      this.authContext()
    );
  }

  extendMembership(businessId: string, request: { days: number }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/extend-membership`,
      request,
      this.authContext()
    );
  }

  setMembershipEndDate(businessId: string, request: { endDate: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/set-membership-end-date`,
      request,
      this.authContext()
    );
  }

  resetBusiness(businessId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/reset`,
      {},
      this.authContext()
    );
  }

  deleteBusiness(businessId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}`,
      this.authContext()
    );
  }

  // Membership Plans
  getMembershipPlans(): Observable<MembershipPlanDto[]> {
    return this.http.get<MembershipPlanDto[]>(
      `${this.baseUrl}/api/v1/system-admin/membership-plans`,
      this.authContext()
    );
  }

  assignMembership(businessId: string, request: { membershipPlanId: string; endDate: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/assign-membership`,
      request,
      this.authContext()
    );
  }

  changeMembershipPlan(businessId: string, request: { newMembershipPlanId: string; transitionDate: string; newEndDate: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/businesses/${businessId}/change-membership-plan`,
      request,
      this.authContext()
    );
  }

  // Impersonation
  impersonateBusiness(businessId: string): Observable<ImpersonateBusinessResponse> {
    return this.http.post<ImpersonateBusinessResponse>(
      `${this.baseUrl}/api/v1/system-admin/impersonate`,
      { businessId },
      this.authContext()
    );
  }

  stopImpersonation(): Observable<StopImpersonationResponse> {
    return this.http.post<StopImpersonationResponse>(
      `${this.baseUrl}/api/v1/system-admin/stop-impersonation`,
      {},
      this.authContext()
    );
  }

  // Audit Log
  getAuditLog(params?: {
    page?: number;
    pageSize?: number;
    actionType?: string;
  }): Observable<GetAuditLogResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
    if (params?.actionType) httpParams = httpParams.set('actionType', params.actionType);

    return this.http.get<GetAuditLogResponse>(
      `${this.baseUrl}/api/v1/system-admin/audit-log`,
      { params: httpParams, ...this.authContext() }
    );
  }

  // Business Types
  getBusinessTypes(params?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
  }): Observable<GetBusinessTypesPagedResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
    if (params?.searchTerm) httpParams = httpParams.set('searchTerm', params.searchTerm);

    return this.http.get<GetBusinessTypesPagedResponse>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/business-types`,
      { params: httpParams, ...this.authContext() }
    );
  }

  createBusinessType(request: CreateBusinessTypeRequest): Observable<{ businessTypeId: string }> {
    return this.http.post<{ businessTypeId: string }>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/business-types`,
      request,
      this.authContext()
    );
  }

  updateBusinessType(id: string, request: UpdateBusinessTypeRequest): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/business-types/${id}`,
      request,
      this.authContext()
    );
  }

  deleteBusinessType(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/business-types/${id}`,
      this.authContext()
    );
  }

  getCustomerTypes(): Observable<CustomerTypeDto[]> {
    return this.http.get<CustomerTypeDto[]>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/customer-types`,
      this.authContext()
    );
  }

  // Countries
  getCountries(params?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
  }): Observable<GetCountriesPagedResponse> {
    let httpParams = new HttpParams();
    if (params?.page) httpParams = httpParams.set('page', params.page.toString());
    if (params?.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
    if (params?.searchTerm) httpParams = httpParams.set('searchTerm', params.searchTerm);

    return this.http.get<GetCountriesPagedResponse>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/countries`,
      { params: httpParams, ...this.authContext() }
    );
  }

  createCountry(request: CreateCountryRequest): Observable<{ countryId: string }> {
    return this.http.post<{ countryId: string }>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/countries`,
      request,
      this.authContext()
    );
  }

  updateCountry(id: string, request: UpdateCountryRequest): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/countries/${id}`,
      request,
      this.authContext()
    );
  }

  deleteCountry(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.baseUrl}/api/v1/system-admin/catalogs/countries/${id}`,
      this.authContext()
    );
  }
}

// Membership Plan
export interface MembershipPlanDto {
  membershipPlanId: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceAnnual: number;
  isActive: boolean;
  numberOfProfessionals: number;
}

// Business Types Interfaces
export interface GetBusinessTypesPagedResponse {
  businessTypes: BusinessTypeDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BusinessTypeDto {
  businessTypeId: string;
  name: string;
  customerTypeName: string;
  customerTypeId: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateBusinessTypeRequest {
  name: string;
  customerTypeId: string;
}

export interface UpdateBusinessTypeRequest {
  name?: string;
  customerTypeId?: string;
}

export interface CustomerTypeDto {
  customerTypeId: string;
  name: string;
}

// Countries Interfaces
export interface GetCountriesPagedResponse {
  countries: CountryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CountryDto {
  countryId: string;
  name: string;
  isoCode: string;
  dialCode: string;
  phoneMask: string;
  phoneHolder: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateCountryRequest {
  name: string;
  isoCode: string;
  dialCode: string;
  phoneMask: string;
  phoneHolder: string;
}

export interface UpdateCountryRequest {
  name?: string;
  isoCode?: string;
  dialCode?: string;
  phoneMask?: string;
  phoneHolder?: string;
}
